import { Box, Button, Divider, Link, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { formatDateTime } from "../../utilities/globalServiceUtils";
import PageTitle from "../PageTitle/PageTitle";
import { ASSETS_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import Datagrid from "../Datagrid/Datagrid";
import { BUTTON, LABELS, TABLE_HEADERS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import useBrandingAssets from "../../hooks/useBrandingAssets";

const RequestHistoryDetails = ({ selectedRow = {}, setViewDetails, viewDetails }) => {
	const navigate = useNavigate();
	const [requestedProducts, setRequestedProducts] = useState([]);
	const {t} = useTranslation();
	const { stockImageUrl } = useBrandingAssets();

	useEffect(() => {
		if (Object.keys(selectedRow).length > 0) {
			setRequestedProducts(selectedRow.products);
		}
	}, [selectedRow]);

    // use global formatDateTime

	const columns = [
		{
			field: "product_name",
			headerName:t('TABLE_HEADERS.PART_NAME'),
			flex: 1,
			renderCell: (params) => {
				const defaultImage = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;

				const handleError = (e) => {
					e.target.onerror = null; // Prevent infinite loop
					e.target.src = defaultImage; // Fallback to default image
				};

				const src = (() => {
					const raw = params.row?.asset_data?.images?.[0] || params.row?.productData?.asset_data?.images?.[0];
					if (raw) {
						if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
						const path = raw.startsWith("/") ? raw : `/${raw}`;
						return `${ASSETS_BASE_URL}${path}`;
					}
					return `${ASSETS_BASE_URL}/ProductImages/TMB/${params.row.product_number}.JPG`;
				})();

				return (
					<div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-start" }}>
						<img
							src={src}
							alt={params.row.product_name}
							onError={handleError}
							style={{
								width: "40px",
								height: "40px",
								objectFit: "cover",
								borderRadius: "4px",
							}}
						/>
						<div>
							<Link
								sx={{ cursor: "pointer" }}
								variant="body2"
								onClick={() => {
									navigate(`/machines/${params.row.machine_id}/${params.row.product_id}`);
								}}>
								{params.row.product_name}
							</Link>
						</div>
					</div>
				);
			},
		},
		{ field: "product_number", headerName:t('UI_TEXT.PART'), width: 300 },
		{ field: "machine_number", headerName:t('LABELS.MACHINE_FORM'), width: 250 },
	];

	const handleCloseRequestHistoryDetails = () => {
		setViewDetails(false);
	};

	return (
		<Box>
			<Divider />
			<PageTitle title={`Request #: ${selectedRow?.request_number || ""}`} />
			<Box justifyContent={"flex-end"} display={"flex"} mb={1}>
				<Button variant="outlined" color="primary" onClick={handleCloseRequestHistoryDetails}>
					{t('BUTTON.CLOSE')}
				</Button>
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "center",
					width: "100%",
					minHeight: "100px",
					height: "auto",

					columnGap: "16px",
				}}>
				<Box
					sx={{
						width: "100%",
						boxShadow: "0 0 10px 2px rgba(0,0,0,0.1)",
						borderRadius: "10px",
						p: 2,
						my: 1,
						minHeight: "100px",
						height: "100%",
					}}>
					<Typography color="primary" variant="h6" fontWeight={"bold"} mb={1}>
						{t('UI_TEXT.REQUEST_DETAILS')}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.REQUESTED')}</b> {selectedRow.request_number || ""}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.REQUESTED_DATE')}</b> {formatDateTime(selectedRow.created) || ""}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.STATUS_HISTORY')}</b> {"Pending"}
					</Typography>
				</Box>
				<Box
					sx={{
						width: "100%",
						boxShadow: "0 0 10px 2px rgba(0,0,0,0.1)",
						borderRadius: "10px",
						p: 2,
						my: 1,
						height: "100%",
						minHeight: "100px",
					}}>
					<Typography color="primary" variant="h6" fontWeight={"bold"} mb={1}>
						{t('UI_TEXT.USER_DETAILS')}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.REQUESTED_BY')}</b> {selectedRow.user_name || ""}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.USER_EMAIL')}</b> {selectedRow.user_email || ""}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.USER_PHONE')}</b> {selectedRow.user_phone || ""}
					</Typography>
				</Box>
				<Box
					sx={{
						width: "100%",
						boxShadow: "0 0 10px 2px rgba(0,0,0,0.1)",
						borderRadius: "10px",
						p: 2,
						my: 1,
						minHeight: "100px",
						height: "100%",
					}}>
					<Typography color="primary" variant="h6" fontWeight={"bold"} mb={1}>
						{t('UI_TEXT.CUSTOMER_DETAILS')}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.CUSTOMER_SITE_NAME')}</b> {selectedRow.site_name || ""}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.CUSTOMER_SITE_NUMBER')}</b> {selectedRow.site_number || ""}
					</Typography>
					<Typography variant="body1" mb={"3px"}>
						<b>{t('UI_TEXT.SHIPPING_ADDRESS')}</b> {selectedRow.address_full_ln || ""}
					</Typography>
				</Box>
			</Box>
			<Datagrid
				key="product_id"
				data={requestedProducts}
				columns={columns}
				rowClick={false}
				pageSize={5}
				showSearchBar={true}
				checkboxSelection={false}
				rowIdField="product_id"
				sortBy={[{ field: "product_name", sort: "asc" }]}
			/>
			<Box justifyContent={"center"} display={"flex"}>
				<Button variant="outlined" color="primary" onClick={handleCloseRequestHistoryDetails}>
					{t('BUTTON.CLOSE')}
				</Button>
			</Box>
		</Box>
	);
};

export default RequestHistoryDetails;
